export type PlayerUser = {
  id: number | string;
  username?: string;
  name?: string;
  /**
   * `users.locale`, captured at registration. There is no endpoint to update it,
   * so treat it as a signup-time snapshot rather than a live preference — see
   * `i18nStore.adoptServerLocale`.
   */
  locale?: string;
  /**
   * `users.country_code` — immutable, and the authority once the player is signed
   * in. Overrides whatever country was selected pre-login; see
   * `countryStore.adoptAccountCountry`.
   */
  country_code?: string;
  displayId?: string;
  display_id?: string;
  wallet_brl?: number;
  walletBrl?: number;
  /** Country-local fiat wallet. Prefer this over the legacy BRL-named aliases. */
  wallet_fiat?: number;
  walletFiat?: number;
  wallet_usdt?: number;
  vip_level?: number;
  vipLevel?: number;
  level?: number;
  avatar_url?: string;
  avatar?: string;
  avatar_default_id?: number;
  avatarDefaultId?: number;
  avatar_thumb?: string;
  avatarThumb?: string;
  avatar_md?: string;
  avatarMd?: string;
  initial?: string;
  public_leaderboard?: boolean;
  publicLeaderboard?: boolean;
  activity_notifications?: boolean;
  activityNotifications?: boolean;
  total_bets?: number;
  totalBets?: number;
  total_won?: number;
  totalWon?: number;
  total_profit_brl?: number;
  totalProfitBrl?: number;
  exp_total?: number;
  total_effective_bet_brl?: number;
  totalEffectiveBet?: number;
  next_vip_level?: number;
  vip_progress_pct?: number;
  next_vip_exp_required?: number;
  next_vip_deposit_required?: number;
  total_deposit_brl?: number;
  next_vip_bet_threshold?: number;
  next_vip_deposit_threshold?: number;
  vip_thresholds?: Record<string, number>;
  vip_tier_names_i18n?: Record<string, string[]> | Record<string, string>;
  vip_tier_name?: { pt?: string; en?: string; es?: string } | string;
  vip_benefits?: {
    levels?: Array<{
      badge?: string;
      label?: string;
      minLevel?: number;
      ticketBonus?: number;
      activityBoostPct?: number;
      rakebackPct?: number;
    }>;
    version?: string;
  };
  phone?: string;
  email?: string;
  phone_verified?: boolean;
  phoneVerified?: boolean;
  email_verified?: boolean;
  emailVerified?: boolean;
  phone_verify_reward_claimed?: boolean;
  phoneVerifyRewardClaimed?: boolean;
  email_verify_reward_claimed?: boolean;
  emailVerifyRewardClaimed?: boolean;
  kyc_status?: string;
  requires_username_setup?: boolean;
  requiresUsernameSetup?: boolean;
  username_changed_at?: number;
  usernameChangedAt?: number;
  username_grace_used?: boolean;
  usernameGraceUsed?: boolean;
  invite_code?: string;
  inviteCode?: string;
  invite_link?: string;
  inviteLink?: string;
  phone_e164?: string;
  phoneE164?: string;
  chatwoot_identifier?: string;
  chatwootIdentifier?: string;
  chatwoot_identifier_hash?: string;
  chatwootIdentifierHash?: string;
};

export type AuthResponse = {
  token: string;
  expires_at?: number;
  expiresAt?: number;
  sessionSecret?: string;
  user?: PlayerUser;
};

export type MeResponse = {
  user: PlayerUser;
};

export function userName(user: PlayerUser | null): string {
  if (!user) return '';
  return user.name || user.username || 'Jogador';
}

export function userWalletBrl(user: PlayerUser | null): number {
  if (!user) return 0;
  return Number(user.wallet_brl ?? user.walletBrl ?? 0);
}

/** The signed-in account's local-fiat balance (ARS for AR, BRL for BR). */
export function userWalletFiat(user: PlayerUser | null): number {
  if (!user) return 0;
  return Number(user.wallet_fiat ?? user.walletFiat ?? user.wallet_brl ?? user.walletBrl ?? 0);
}

export function userVipLevel(user: PlayerUser | null): number {
  if (!user) return 1;
  return Number(user.vip_level ?? user.vipLevel ?? user.level ?? 1) || 1;
}

export function userDisplayId(user: PlayerUser | null): string {
  const raw = String(user?.display_id ?? user?.displayId ?? '').trim();
  if (!raw) return '';
  return raw.toUpperCase().startsWith('S') ? raw : `S${raw}`;
}
